import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

/** How far the finger must travel sideways for the swipe to act on release. */
const THRESHOLD_PX = 64;
/** The furthest the element follows the finger. */
const MAX_PX = 96;
/** How far the finger moves before the gesture is judged a swipe or a scroll. */
const DECIDE_PX = 8;
/** Presses this close to the screen's sides belong to the phone's back gesture. */
const EDGE_PX = 24;

/**
 * Drags the element sideways under a touch or pen, WhatsApp style, and emits when it is let go past the threshold.
 * Up-and-down movement is left to the page's scroll, and the mouse is ignored. While dragging the host carries
 * `data-swipe="right|left"`, `--swipe-progress` (0 to 1) and, once far enough, the `swipe-ready` class, for the
 * icon that fades in behind it.
 *
 * Usage: <div (appSwipeRight)="reply()" (appSwipeLeft)="file()" [swipeLeftEnabled]="canFile">
 */
@Directive({ selector: '[appSwipeRight],[appSwipeLeft]', standalone: true })
export class SwipeActionsDirective implements OnInit, OnDestroy {
  @Input() swipeRightEnabled = true;
  @Input() swipeLeftEnabled = true;
  @Output() readonly appSwipeRight = new EventEmitter<void>();
  @Output() readonly appSwipeLeft = new EventEmitter<void>();

  private pointerId: number | null = null;
  private downX = 0;
  private downY = 0;
  private dragging = false;
  private ready = false;
  private swallowNextClick = false;
  private teardown: Array<() => void> = [];

  constructor(private elementRef: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  ngOnInit(): void {
    // Sideways drags come to the page; up and down still scroll natively.
    this.elementRef.nativeElement.style.touchAction = 'pan-y';
    // Outside the zone: pointermove fires every frame and changes nothing the view binds to.
    this.ngZone.runOutsideAngular(() => {
      this.listen('pointerdown', (e: PointerEvent) => this.onDown(e));
      this.listen('pointermove', (e: PointerEvent) => this.onMove(e));
      this.listen('pointerup', (e: PointerEvent) => this.onEnd(e, true));
      this.listen('pointercancel', (e: PointerEvent) => this.onEnd(e, false));
      // Once the drag is sideways, the thread must not scroll under it.
      this.listen('touchmove', (e: TouchEvent) => { if (this.dragging && e.cancelable) e.preventDefault(); }, false, false);
      this.listen('click', (e: MouseEvent) => this.onClick(e), true);
    });
  }

  ngOnDestroy(): void {
    this.reset();
    this.teardown.forEach((off) => off());
  }

  private listen(type: string, handler: (e: any) => void, capture = false, passive?: boolean): void {
    const host = this.elementRef.nativeElement;
    const options = passive === undefined ? capture : { capture, passive };
    host.addEventListener(type, handler, options);
    this.teardown.push(() => host.removeEventListener(type, handler, options));
  }

  private onDown(e: PointerEvent): void {
    this.swallowNextClick = false;
    if (e.pointerType === 'mouse' || !e.isPrimary) return;
    if (e.clientX < EDGE_PX || e.clientX > window.innerWidth - EDGE_PX) return;
    this.reset();
    this.pointerId = e.pointerId;
    this.downX = e.clientX;
    this.downY = e.clientY;
  }

  private onMove(e: PointerEvent): void {
    if (e.pointerId !== this.pointerId) return;
    const dx = e.clientX - this.downX;
    const dy = e.clientY - this.downY;
    if (!this.dragging) {
      if (Math.hypot(dx, dy) < DECIDE_PX) return;
      // A clear sideways drag, at least twice as wide as it is tall; anything else is a scroll.
      if (Math.abs(dx) < 2 * Math.abs(dy) || !this.allowed(dx)) {
        this.pointerId = null;
        return;
      }
      this.dragging = true;
    }
    this.show(this.allowed(dx) ? dx : 0);
  }

  private onEnd(e: PointerEvent, released: boolean): void {
    if (e.pointerId !== this.pointerId) return;
    const dx = e.clientX - this.downX;
    const act = released && this.dragging && this.allowed(dx) && Math.abs(dx) >= THRESHOLD_PX;
    if (this.dragging) this.swallowNextClick = true;
    this.reset();
    if (act) this.ngZone.run(() => (dx > 0 ? this.appSwipeRight : this.appSwipeLeft).emit());
  }

  private onClick(e: MouseEvent): void {
    if (!this.swallowNextClick) return;
    this.swallowNextClick = false;
    e.preventDefault();
    e.stopPropagation();
  }

  private allowed(dx: number): boolean {
    return dx > 0 ? this.swipeRightEnabled : this.swipeLeftEnabled;
  }

  /** Follows the finger one to one up to the threshold, then with growing resistance up to the limit. */
  private show(dx: number): void {
    const host = this.elementRef.nativeElement;
    const distance = Math.abs(dx);
    const moved = distance <= THRESHOLD_PX ? distance : Math.min(MAX_PX, THRESHOLD_PX + (distance - THRESHOLD_PX) * 0.3);
    const ready = distance >= THRESHOLD_PX;
    if (ready && !this.ready) navigator.vibrate?.(10);
    this.ready = ready;
    host.style.transition = 'none';
    host.style.transform = moved ? `translateX(${Math.sign(dx) * moved}px)` : '';
    host.style.setProperty('--swipe-progress', String(Math.min(1, distance / THRESHOLD_PX)));
    if (moved) host.setAttribute('data-swipe', dx > 0 ? 'right' : 'left');
    else host.removeAttribute('data-swipe');
    host.classList.toggle('swipe-ready', ready);
  }

  private reset(): void {
    const host = this.elementRef.nativeElement;
    if (this.dragging) host.style.transition = 'transform 180ms ease-out';
    host.style.transform = '';
    host.style.removeProperty('--swipe-progress');
    host.removeAttribute('data-swipe');
    host.classList.remove('swipe-ready');
    this.pointerId = null;
    this.dragging = false;
    this.ready = false;
  }
}
